/**
 * DocumentOutboxPage — Jul-2026 (User-Wunsch).
 *
 * „Dokumenten-Postausgang": überwachter Desktop-Ordner. Dateien, die dort
 * abgelegt werden, ordnet die KI automatisch einem Fahrzeug/Kunden zu,
 * archiviert sie am Fahrzeug und mailt sie dem Kunden (Anhang + Download-
 * Link). Nach erfolgreicher Verarbeitung wird die Datei aus dem Ordner
 * GELÖSCHT. Läuft, solange dieser Browser-Tab geöffnet ist.
 *
 * Route: /dealer/documents/outbox
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    FolderOpen, ArrowLeft, Loader2, Check, X as XIcon, Mail, AlertCircle,
    FileText, Play, Square, Car, User as UserIcon, Trash2, History as HistoryIcon,
    Eye, Ban, ScanLine, Wrench, PlusCircle, Search, Settings, ClipboardList, EyeOff, Camera,
} from "lucide-react";

import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useInboxWatcher } from "../lib/inboxWatcher";
import { scanFolderSupported, getOutboxFolder, getStoredOutboxFolder, listOutboxFiles, deleteOutboxFile } from "../lib/scanFolder";
import OutboxMobileQrCard from "../components/documents/OutboxMobileQrCard";
import { runScanJob } from "../lib/scanJobs";
import { setHandoffFile, setHandoffCleanup } from "../lib/fileHandoff";
import { useWatchHeartbeat } from "../lib/useWatchHeartbeat";
import { ScanProgress } from "../components/ScanProgress";
import { Button } from "../components/ui/button";
import { OutboxSettingsModal } from "../components/dealer/OutboxSettingsModal";
import { useGearsVisible } from "../lib/gearVisibility";

function ActionBtn({ onClick, icon: Icon, label, primary, danger, testId }) {
    return (
        <button type="button" onClick={onClick}
            className={`inline-flex items-center gap-1 text-[11px] font-bold rounded-sm px-2 py-1.5 border ${primary ? "bg-[#111827] text-[#FFCC00] border-[#111827] hover:bg-black" : danger ? "border-red-300 text-red-700 bg-red-50 hover:bg-red-100" : "border-gray-300 text-gray-700 bg-white hover:border-gray-500"}`}
            data-testid={testId}>
            <Icon className="w-3 h-3" /> {label}
        </button>
    );
}

// Jul-2026 (User-Wunsch) — manuelle Archivierung: Kunde suchen → am Kunden
// oder an einem seiner Garage-Fahrzeuge archivieren (ohne E-Mail-Versand).
function ArchivePanel({ item, onArchive }) {
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [selected, setSelected] = useState(null);
    const [vehicles, setVehicles] = useState([]);
    useEffect(() => {
        if (q.trim().length < 2) { setResults([]); return undefined; }
        const t = setTimeout(async () => {
            try {
                const r = await api.get("/dealer/customers/search", { params: { q: q.trim() } });
                setResults((r.data?.customers || []).slice(0, 6));
            } catch { setResults([]); }
        }, 300);
        return () => clearTimeout(t);
    }, [q]);
    const pick = async (c) => {
        setSelected(c);
        setResults([]);
        try {
            const r = await api.get(`/customers/${c.id}/vehicles`);
            setVehicles(Array.isArray(r.data) ? r.data : (r.data?.items || []));
        } catch { setVehicles([]); }
    };
    const label = (c) => c.company_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || "Kunde";
    return (
        <div className="bg-gray-50 border border-gray-200 rounded-sm p-2.5 space-y-2" data-testid={`outbox-archive-panel-${item.token}`}>
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-600 inline-flex items-center gap-1">
                <Search className="w-3 h-3" /> Kunde suchen (Name, Firma, E-Mail, FIN, Kennzeichen)
            </p>
            <input value={q} onChange={(e) => { setQ(e.target.value); setSelected(null); }}
                placeholder="z. B. Müller oder WVW1234…"
                className="w-full md:w-96 border border-gray-300 rounded-sm px-2 py-1.5 text-sm bg-white"
                data-testid={`outbox-archive-search-${item.token}`} />
            {results.length > 0 && !selected && (
                <div className="space-y-1">
                    {results.map((c) => (
                        <button key={c.id} type="button" onClick={() => pick(c)}
                            className="block w-full text-left text-xs bg-white border border-gray-200 rounded-sm px-2 py-1.5 hover:border-[#FFCC00]"
                            data-testid={`outbox-archive-result-${c.id}`}>
                            <b>{label(c)}</b>
                            {c.email ? <span className="text-gray-400"> · {c.email}</span> : null}
                            {c.customer_number ? <span className="text-gray-400"> · Kd-Nr. {c.customer_number}</span> : null}
                        </button>
                    ))}
                </div>
            )}
            {selected && (
                <div className="space-y-1.5">
                    <p className="text-xs"><b>{label(selected)}</b> ausgewählt — wo archivieren?</p>
                    <div className="flex flex-wrap gap-1.5">
                        <button type="button"
                            onClick={() => onArchive({ customer_id: selected.id, customer_name: label(selected), vehicle_id: null, vehicle_label: null, customer_email: selected.email || "" }, false)}
                            className="text-[11px] font-bold bg-emerald-600 text-white rounded-sm px-2.5 py-1.5 hover:bg-emerald-700"
                            data-testid={`outbox-archive-customer-${item.token}`}>
                            Nur am Kunden archivieren
                        </button>
                        {vehicles.map((v) => (
                            <button key={v.id} type="button"
                                onClick={() => onArchive({ customer_id: selected.id, customer_name: label(selected), vehicle_id: v.id, vehicle_label: `${v.brand || ""} ${v.model || ""}`.trim(), customer_email: selected.email || "" }, false)}
                                className="text-[11px] font-bold bg-white border border-emerald-300 text-emerald-800 rounded-sm px-2.5 py-1.5 hover:bg-emerald-50"
                                data-testid={`outbox-archive-vehicle-${v.id}`}>
                                <Car className="w-3 h-3 inline mr-1" /> {`${v.brand || ""} ${v.model || ""}`.trim()}{v.license_plate ? ` · ${v.license_plate}` : ""}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Jul-2026 (User-Wunsch) — „Neu zuordnen" im Verlauf: KI prüft das archivierte
// PDF erneut auf mögliche Zuordnungen; alternativ manuelle Kundensuche.
// Standard: KEINE E-Mail beim Neu-Zuordnen (per Haken aktivierbar).
function HistoryReassignPanel({ entry, onClose, onDone }) {
    const [checking, setChecking] = useState(true);
    const [result, setResult] = useState(null);
    const [sendEmail, setSendEmail] = useState(false);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        let alive = true;
        api.post(`/documents/outbox/recheck/${entry.token}`)
            .then((r) => { if (alive) setResult(r.data); })
            .catch((e) => {
                if (alive) {
                    toast.error(e.response?.data?.detail || "Erneute Prüfung fehlgeschlagen");
                    setResult({ matches: [] });
                }
            })
            .finally(() => { if (alive) setChecking(false); });
        return () => { alive = false; };
    }, [entry.token]);
    const assign = async (match) => {
        setBusy(true);
        try {
            const { data } = await api.post("/documents/outbox/commit", {
                token: entry.token, customer_id: match.customer_id,
                vehicle_id: match.vehicle_id || null, send_email: sendEmail,
            });
            toast.success(`${entry.filename} → ${match.customer_name}`
                + (data.emailed ? ` · E-Mail an ${data.to_email}${data.email_status === "freigabe" ? " (E-Mail-Freigabe)" : data.email_status === "evening" ? " (Abend-Sammel-Mail)" : ""}` : " · ohne E-Mail")
                + (data.vehicle_updates?.hu_date ? ` · TÜV bis ${data.vehicle_updates.hu_date} am Fahrzeug aktualisiert` : "")
                + (Object.keys(data.vehicle_updates || {}).filter((k) => k !== "hu_date").length ? " · Fahrzeugdaten ergänzt" : ""));
            onDone();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        }
        setBusy(false);
    };
    return (
        <div className="w-full mt-1.5 bg-indigo-50/60 border border-indigo-200 rounded-sm p-3 space-y-2.5" data-testid={`outbox-reassign-panel-${entry.token}`}>
            <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-900 inline-flex items-center gap-1.5">
                    <ScanLine className="w-3.5 h-3.5" /> Neu zuordnen — „{entry.filename}"
                </p>
                <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-700" data-testid={`outbox-reassign-close-${entry.token}`} aria-label="Schließen">
                    <XIcon className="w-3.5 h-3.5" />
                </button>
            </div>
            {checking ? (
                <p className="text-xs text-indigo-900 inline-flex items-center gap-1.5" data-testid={`outbox-reassign-checking-${entry.token}`}>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> KI prüft das Dokument erneut …
                </p>
            ) : (
                <>
                    {result?.fields && (result.fields.vin || result.fields.license_plate || result.fields.last_name) && (
                        <p className="text-[11px] text-indigo-900">
                            Erkannt: {[result.fields.vin && `FIN ${result.fields.vin}`,
                                result.fields.license_plate && `Kennzeichen ${result.fields.license_plate}`,
                                result.fields.last_name && `Name „${result.fields.last_name}"`].filter(Boolean).join(" · ")}
                        </p>
                    )}
                    {(result?.matches || []).length > 0 ? (
                        <div className="space-y-1">
                            <p className="text-[11px] font-bold text-indigo-900">KI-Vorschläge:</p>
                            <div className="flex flex-wrap gap-1.5">
                                {result.matches.map((m, i) => (
                                    <button key={i} type="button" disabled={busy} onClick={() => assign(m)}
                                        className="text-[11px] font-bold bg-[#111827] text-[#FFCC00] rounded-sm px-2.5 py-1.5 hover:bg-black disabled:opacity-50"
                                        data-testid={`outbox-reassign-match-${entry.token}-${i}`}>
                                        <UserIcon className="w-3 h-3 inline mr-1" />
                                        {m.customer_name}{m.vehicle_label ? ` · ${m.vehicle_label}` : ""}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1 inline-flex items-center gap-1.5" data-testid={`outbox-reassign-nomatch-${entry.token}`}>
                            <AlertCircle className="w-3.5 h-3.5" /> Keine automatische Zuordnung gefunden — bitte unten manuell zuweisen.
                        </p>
                    )}
                    <label className="flex items-center gap-2 text-[11px] text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)}
                            className="w-3.5 h-3.5 accent-[#111827]" data-testid={`outbox-reassign-email-${entry.token}`} />
                        <span>E-Mail mit dem Dokument an den (neuen) Kunden senden</span>
                    </label>
                    <ArchivePanel item={{ token: entry.token }} onArchive={(match) => assign(match)} />
                </>
            )}
        </div>
    );
}

export default function DocumentOutboxPage({ embedded = false }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    // Aug-2026 (Dokumenten-Center): eingebettet liefert der globale Watcher die
    // zu prüfenden Kunden-Dokumente; Ordner-Logik läuft dann NUR im Watcher.
    const watcher = useInboxWatcher();
    const [folderName, setFolderName] = useState("");
    const [watching, setWatching] = useState(false);
    const [autoSend, setAutoSend] = useState(true);
    const [pending, setPending] = useState([]); // Dateien mit unklarer Zuordnung
    const [analyzing, setAnalyzing] = useState(null); // {name, phase} — laufende KI-Analyse
    const [log, setLog] = useState([]); // verarbeitete Einträge
    const [history, setHistory] = useState(null); // persistenter Verlauf (Server)
    const [historyDays, setHistoryDays] = useState(30);
    const handleRef = useRef(null);
    const processedRef = useRef(new Set());
    const busyRef = useRef(false);
    const watchRef = useRef(false);
    const [needsReconnect, setNeedsReconnect] = useState(false);
    const [ignoreRules, setIgnoreRules] = useState([]);
    const [archiveFor, setArchiveFor] = useState(null); // token mit offenem Archiv-Panel
    const [reassignToken, setReassignToken] = useState(null); // Verlauf: „Neu zuordnen"-Panel
    const [settingsOpen, setSettingsOpen] = useState(false); // Zahnrad (Jul-2026)
    const [protokoll, setProtokoll] = useState(null); // Protokoll inkl. ignorierter (Jul-2026)
    // Nov-2026 (User-Wunsch) — Auto-Vorgang: erkannter Dokumenttyp öffnet den
    // passenden Vorgang (Lieferschein-Scan, Scan-Erfassung, Upload-Center).
    const [autoVorgang, setAutoVorgang] = useState(() => localStorage.getItem("tw_outbox_auto_vorgang") !== "0");
    const gearsOk = useGearsVisible();

    const loadProtokoll = useCallback((days) => {
        api.get(`/documents/outbox/history?days=${days}&include_hidden=1`)
            .then((r) => setProtokoll(r.data.items || []))
            .catch(() => setProtokoll([]));
    }, []);
    useEffect(() => { loadProtokoll(historyDays); }, [historyDays, loadProtokoll]);

    const hideEntry = async (token) => {
        try {
            await api.post(`/documents/outbox/history/${token}/hide`);
            toast.success("Eintrag ignoriert — über das Protokoll wieder aufrufbar");
            loadHistory(historyDays); loadProtokoll(historyDays);
        } catch { toast.error("Ignorieren fehlgeschlagen"); }
    };
    const unhideEntry = async (token) => {
        try {
            await api.post(`/documents/outbox/history/${token}/unhide`);
            toast.success("Eintrag wieder eingeblendet");
            loadHistory(historyDays); loadProtokoll(historyDays);
        } catch { toast.error("Einblenden fehlgeschlagen"); }
    };
    const ignoreSetRef = useRef(new Set());

    useWatchHeartbeat("outbox", watching, folderName);

    if (user === false) navigate("/login");

    const pendingItems = embedded ? (watcher?.pendingCustomer || []) : pending;

    const addLog = (entry) => {
        if (embedded && watcher?.addLog) { watcher.addLog({ channel: "outbox", ...entry }); return; }
        setLog((prev) => [{ ...entry, at: new Date() }, ...prev].slice(0, 50));
    };

    const removePending = (token) => {
        if (embedded) watcher?.removeCustomerItem(token);
        else setPending((prev) => prev.filter((p) => p.token !== token));
    };

    const delFolderFile = async (fileName) => {
        if (embedded) return watcher?.deleteFromFolder(fileName);
        return handleRef.current ? deleteOutboxFile(handleRef.current, fileName) : false;
    };

    const loadHistory = useCallback(async (days) => {
        try {
            const { data } = await api.get(`/documents/outbox/history?days=${days}`);
            setHistory(data.items || []);
        } catch {
            setHistory([]);
        }
    }, []);

    useEffect(() => { loadHistory(historyDays); }, [historyDays, loadHistory]);
    // eingebettet: nach jeder Watcher-Verarbeitung den Verlauf aktualisieren
    useEffect(() => { if (embedded) loadHistory(historyDays); },
        [embedded, watcher?.historyReload, historyDays, loadHistory]);

    const loadIgnore = useCallback(async () => {
        try {
            const { data } = await api.get("/documents/outbox/ignore");
            const rules = data.items || [];
            setIgnoreRules(rules);
            ignoreSetRef.current = new Set(rules.map((r) => r.filename_lc));
        } catch { /* noop */ }
    }, []);
    useEffect(() => { loadIgnore(); }, [loadIgnore]);

    // Aug-2026 (User-Wunsch Jörg): fertige Handy-Uploads (Filial-QR) abholen
    useEffect(() => {
        if (embedded) return undefined; // übernimmt der globale Watcher
        const tick = async () => {
            try {
                const { data } = await api.get("/documents/outbox/mobile-uploads");
                for (const job of data.items || []) {
                    await api.post(`/documents/outbox/mobile-uploads/${job.id}/consume`).catch(() => {});
                    if (job.status === "error") {
                        addLog({ ok: false, name: job.filename || "Handy-Upload", error: job.error || "Analyse fehlgeschlagen" });
                        continue;
                    }
                    const item = { ...(job.result || {}), fileName: job.filename || job.result?.filename || "Handy-Upload", mobile: true };
                    setPending((prev) => (prev.some((p) => p.token === item.token) ? prev : [...prev, item]));
                    toast.success(`Beleg vom Handy empfangen: ${item.fileName} — bitte Zuordnung prüfen`);
                }
            } catch { /* noop */ }
        };
        tick();
        const t = setInterval(tick, 6000);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Jul-2026 (User-Wunsch): Überwachung standardmäßig aktiv — gemerkten
    // Ordner beim Öffnen automatisch wieder verbinden (wenn Rechte bestehen).
    useEffect(() => {
        (async () => {
            if (embedded || !scanFolderSupported()) return;
            const stored = await getStoredOutboxFolder();
            if (!stored) return;
            if (stored.needsPermission) {
                setFolderName(stored.name || "");
                setNeedsReconnect(true);
                return;
            }
            handleRef.current = stored;
            setFolderName(stored.name);
            setWatching(true);
        })();
    }, []);

    const commit = useCallback(async (item, match, sendEmail) => {
        try {
            const { data } = await api.post("/documents/outbox/commit", {
                token: item.token,
                customer_id: match.customer_id,
                vehicle_id: match.vehicle_id || null,
                send_email: sendEmail,
            });
            const removed = await delFolderFile(item.fileName);
            addLog({
                ok: true, name: item.fileName, customer: match.customer_name,
                vehicle: match.vehicle_label,
                emailed: data.emailed, mocked: data.mocked, emailStatus: data.email_status,
                to: data.to_email, removed,
            });
            removePending(item.token);
            loadHistory(historyDays);
            const mailInfo = !data.emailed ? ""
                : data.email_status === "freigabe"
                    ? ` · E-Mail an ${data.to_email} wartet in der E-Mail-Freigabe`
                    : ` · E-Mail an ${data.to_email}${data.mocked ? " (MOCK)" : ""}`;
            const vehInfo = (data.vehicle_updates?.hu_date ? ` · TÜV bis ${data.vehicle_updates.hu_date} aktualisiert` : "")
                + (Object.keys(data.vehicle_updates || {}).filter((k) => k !== "hu_date").length ? " · Fahrzeugdaten ergänzt" : "");
            toast.success(
                `${item.fileName} → ${match.customer_name}`
                + mailInfo
                + vehInfo
                + (removed ? " · aus Ordner gelöscht" : ""));
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuordnung fehlgeschlagen");
        }
    }, [loadHistory, historyDays]);

    // Nov-2026 (User-Wunsch) — Datei für einen Folge-Vorgang beschaffen
    // (lokal aus dem Ordner oder aus dem Server-Archiv).
    const fetchItemFile = useCallback(async (item) => {
        try {
            if (embedded && watcher?.getFolderFile) {
                const wf = await watcher.getFolderFile(item.fileName);
                if (wf) return wf;
            } else if (handleRef.current) {
                const fh = await handleRef.current.getFileHandle(item.fileName);
                return await fh.getFile();
            }
        } catch { /* nicht (mehr) im Ordner */ }
        try {
            const res = await fetch(`${api.defaults.baseURL}/documents/dl/${item.token}`);
            if (res.ok) return new File([await res.blob()], item.fileName, { type: item.mime || "application/pdf" });
        } catch { /* noop */ }
        return null;
    }, []);

    const gotoVorgang = useCallback(async (item, path, label) => {
        const f = await fetchItemFile(item);
        if (!f) { toast.error("Datei nicht verfügbar"); return false; }
        // Aug-2026 (User-Bug): NICHT sofort aus dem Ordner löschen — erst wenn
        // der Folge-Vorgang (z. B. Lieferschein-Scan) erfolgreich war. Sonst
        // ist die Datei bei einem Upload-Fehler unwiederbringlich weg.
        const handle = handleRef.current;
        setHandoffCleanup(f, embedded
            ? () => watcher?.deleteFromFolder(item.fileName)
            : (handle ? () => deleteOutboxFile(handle, item.fileName) : null));
        setHandoffFile(f);
        toast.info(`${item.fileName}: ${label} wird geöffnet …`);
        navigate(path);
        return true;
    }, [fetchItemFile, navigate]);

    const toDeliveryScan = useCallback(
        (item) => gotoVorgang(item, "/dealer/wms/scan-delivery", "Lieferschein-Scan"), [gotoVorgang]);
    const toScanErfassung = useCallback(
        (item) => gotoVorgang(item, "/dealer/workshop-orders?action=zb1-scan", "Scan-Erfassung"), [gotoVorgang]);

    // Nov-2026 (User-Wunsch) — Lohnbescheinigung/Ausweis/Pass/Meldebescheinigung
    // automatisch in die „Upload Belege" des passenden Verkaufs hochladen.
    const toBelege = useCallback(async (item, customerId, silent = false) => {
        try {
            const { data } = await api.post("/documents/outbox/to-belege", {
                token: item.token, customer_id: customerId || undefined,
            });
            const removed = await delFolderFile(item.fileName);
            addLog({
                ok: true, name: item.fileName, removed,
                action: `Upload-Center: „${data.label}" → Verkauf ${data.vehicle_label || ""} (${data.customer_name})`,
            });
            removePending(item.token);
            loadHistory(historyDays);
            toast.success(`${item.fileName} → Upload-Belege „${data.label}" am Verkauf${data.vehicle_label ? ` ${data.vehicle_label}` : ""}${removed ? " · aus Ordner gelöscht" : ""}`);
            return true;
        } catch (e) {
            if (!silent) toast.warning(e.response?.data?.detail || "Kein passender Verkauf gefunden — bitte manuell zuordnen");
            return false;
        }
    }, [loadHistory, historyDays]);

    const processFile = useCallback(async (file) => {
        setAnalyzing({ name: file.name, phase: "upload" });
        try {
            const data = await runScanJob("outbox", file, {
                onPhase: (p) => setAnalyzing({ name: file.name, phase: p }),
            });
            const item = { ...data, fileName: file.name };
            // Nov-2026 (User-Wunsch) — Auto-Vorgang je erkanntem Dokumenttyp.
            const action = data.suggested_action;
            if (autoVorgang && action === "delivery_scan") {
                setAnalyzing(null);
                if (await toDeliveryScan(item)) return;
            } else if (autoVorgang && action === "scan_erfassung") {
                setAnalyzing(null);
                if (await toScanErfassung(item)) return;
            } else if (action === "belege_upload") {
                const cid = data.auto_match?.customer_id
                    || ((data.matches || []).length >= 1 ? data.matches[0].customer_id : null);
                if (autoVorgang && cid && (data.matches || []).length === 1) {
                    if (await toBelege(item, cid, true)) { setAnalyzing(null); return; }
                }
                setPending((prev) => prev.some((p) => p.fileName === file.name) ? prev : [...prev, item]);
                if ((data.matches || []).length === 0) {
                    toast.warning(`${file.name}: ${data.document_type} erkannt, aber kein Kunde gefunden — bitte manuell zuordnen`);
                }
                setAnalyzing(null);
                return;
            }
            // Jul-2026 (Zahnrad): E-Mail-Regel je Dokumenttyp — „never" sendet
            // nie, „ask" hält zur manuellen Bestätigung an, „auto" wie gehabt,
            // „evening" sammelt für die Abend-E-Mail (1 Mail pro Kunde).
            const mode = data.email_mode || "ask";
            if (data.auto_match && autoSend && !data.is_supplier_doc && mode !== "ask") {
                await commit(item, data.auto_match, mode === "auto" || mode === "evening");
            } else {
                setPending((prev) => prev.some((p) => p.fileName === file.name) ? prev : [...prev, item]);
                if ((data.matches || []).length === 0) {
                    toast.warning(`${file.name}: keine Zuordnung gefunden — bitte manuell zuordnen`);
                }
            }
        } catch (e) {
            processedRef.current.delete(file.name);
            addLog({ ok: false, name: file.name, error: e.response?.data?.detail || e.message || "Analyse fehlgeschlagen" });
        }
        setAnalyzing(null);
    }, [autoSend, autoVorgang, commit, toDeliveryScan, toScanErfassung, toBelege]);

    const pollOnce = useCallback(async () => {
        if (!handleRef.current || busyRef.current) return;
        busyRef.current = true;
        try {
            const files = await listOutboxFiles(handleRef.current);
            for (const f of files) {
                if (processedRef.current.has(f.name)) continue;
                if (ignoreSetRef.current.has(f.name.toLowerCase())) continue;
                processedRef.current.add(f.name);
                // eslint-disable-next-line no-await-in-loop
                await processFile(f);
            }
        } catch { /* Ordner ggf. kurz gesperrt */ }
        busyRef.current = false;
    }, [processFile]);

    useEffect(() => {
        watchRef.current = watching;
        if (!watching) return undefined;
        pollOnce();
        const t = setInterval(pollOnce, 4000);
        return () => clearInterval(t);
    }, [watching, pollOnce]);

    // Aug-2026 (User-Wunsch): Daueröberwachung — beim Zurückkehren in den Tab
    // sofort nachsehen (Browser drosseln Timer in Hintergrund-Tabs).
    useEffect(() => {
        const onVis = () => { if (!document.hidden && watchRef.current) pollOnce(); };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [pollOnce]);

    const connect = async (forcePick = false) => {
        if (!scanFolderSupported()) {
            toast.error("Dieser Browser unterstützt keine Ordner-Überwachung (Chrome/Edge nötig).");
            return;
        }
        try {
            const handle = await getOutboxFolder(forcePick);
            if (!handle) return;
            handleRef.current = handle;
            setFolderName(handle.name);
            setWatching(true);
            setNeedsReconnect(false);
            toast.success(`Ordner „${handle.name}" verbunden — Überwachung läuft`);
        } catch (e) {
            if (e?.name !== "AbortError") toast.error("Ordner konnte nicht verbunden werden");
        }
    };

    // ── Jul-2026 (User-Wunsch) — Weiterverarbeitung / Ansehen / Ignorieren / Löschen ──
    const openDoc = (item) => window.open(`${api.defaults.baseURL}/documents/dl/${item.token}`, "_blank", "noopener");

    const deleteDoc = async (item) => {
        if (!window.confirm(`ACHTUNG / VORSICHT: Das Dokument „${item.fileName}" wird endgültig GELÖSCHT — auch aus dem Ordner auf dem PC. Fortfahren?`)) return;
        try {
            await api.delete(`/documents/outbox/${item.token}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen — Dokument bleibt erhalten");
            return;
        }
        const removed = await delFolderFile(item.fileName);
        removePending(item.token);
        addLog({ ok: true, name: item.fileName, action: "gelöscht", removed });
        toast.success(`${item.fileName} gelöscht${removed ? " · aus Ordner entfernt" : ""}`);
    };

    const ignoreDoc = async (item) => {
        if (!window.confirm(`ACHTUNG / VORSICHT: „${item.fileName}" wird dauerhaft IGNORIERT — alle in Zukunft neu eingehenden Dokumente mit diesem Dateinamen werden nicht mehr erkannt und nicht mehr verarbeitet. Die Datei wird auch aus dem Ordner auf dem PC entfernt. Fortfahren?`)) return;
        try {
            await api.post("/documents/outbox/ignore", { filename: item.fileName, token: item.token });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ignorieren fehlgeschlagen");
            return;
        }
        ignoreSetRef.current.add(item.fileName.toLowerCase());
        const removed = await delFolderFile(item.fileName);
        removePending(item.token);
        addLog({ ok: true, name: item.fileName, action: "dauerhaft ignoriert", removed });
        loadIgnore();
        toast.success(`${item.fileName} wird künftig ignoriert${removed ? " · aus Ordner entfernt" : ""}`);
    };

    const unignore = async (rule) => {
        try {
            await api.delete(`/documents/outbox/ignore/${rule.id}`);
            loadIgnore();
            toast.success(`„${rule.filename}" wird wieder verarbeitet`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Konnte Regel nicht entfernen");
        }
    };

    return (
        <div className={embedded ? "" : "min-h-screen bg-gray-50"} data-testid="document-outbox-page">
            {!embedded && (
            <div className="bg-[#111827] text-white">
                <div className="max-w-5xl mx-auto px-4 md:px-8 py-8">
                    <Link to="/dealer/invoices" className="text-xs text-gray-400 hover:text-white inline-flex items-center gap-1" data-testid="outbox-back">
                        <ArrowLeft className="w-3 h-3" /> Zurück zu Rechnungen &amp; Kunden
                    </Link>
                    <div className="mt-2 flex items-center gap-3">
                        <FolderOpen className="w-7 h-7 text-[#FFCC00]" />
                        <div>
                            <h1 className="font-display font-black text-4xl md:text-5xl inline-flex items-center gap-3">
                                Dokumenten-Postausgang
                                {gearsOk && (
                                    <button type="button" onClick={() => setSettingsOpen(true)}
                                        className="p-2 rounded-sm border border-gray-600 text-gray-300 hover:text-white hover:border-white transition-colors"
                                        title="Einstellungen: E-Mail-Regeln je Dokumenttyp & Ignorier-Regeln"
                                        data-testid="outbox-settings-gear">
                                        <Settings className="w-5 h-5" />
                                    </button>
                                )}
                            </h1>
                            <p className="text-sm text-gray-300 mt-1">
                                Desktop-Ordner überwachen → KI ordnet Dokumente Fahrzeug &amp; Kunde zu → Archiv am Fahrzeug + E-Mail an den Kunden → Datei wird aus dem Ordner gelöscht
                            </p>
                        </div>
                        {/* Aug-2026 (User-Wunsch Jörg): Filial-QR — Belege per Handy hochladen */}
                        <div className="ml-auto self-start">
                            <OutboxMobileQrCard />
                        </div>
                    </div>
                </div>
            </div>
            )}

            <div className={embedded ? "space-y-5" : "max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-5"}>
                {!embedded && (
                <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="outbox-connect-block">
                    <div className="flex flex-col lg:flex-row gap-5">
                        <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-3">
                                {!watching ? (
                                    <Button onClick={() => connect(false)} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-black rounded-sm uppercase tracking-wider" data-testid="outbox-connect-btn">
                                        <Play className="w-4 h-4 mr-1.5" /> Ordner verbinden &amp; überwachen
                                    </Button>
                                ) : (
                                    <Button onClick={() => setWatching(false)} variant="outline" className="rounded-sm uppercase tracking-wider border-2 border-red-500 text-red-700 font-bold" data-testid="outbox-stop-btn">
                                        <Square className="w-4 h-4 mr-1.5" /> Überwachung stoppen
                                    </Button>
                                )}
                                {folderName && (
                                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold rounded-sm px-2.5 py-1.5 border ${watching ? "bg-green-50 text-green-800 border-green-300" : "bg-gray-50 text-gray-600 border-gray-300"}`} data-testid="outbox-status">
                                        <span className={`w-2 h-2 rounded-full ${watching ? "bg-green-500 animate-pulse" : "bg-gray-400"}`} />
                                        Ordner: <b>{folderName}</b> {watching ? "— wird überwacht" : "— gestoppt"}
                                    </span>
                                )}
                                {folderName && (
                                    <button type="button" onClick={() => connect(true)} className="text-xs underline text-gray-500 hover:text-gray-700" data-testid="outbox-change-folder">
                                        anderen Ordner wählen
                                    </button>
                                )}
                                {needsReconnect && !watching && (
                                    <button type="button" onClick={() => connect(false)}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold bg-amber-100 text-amber-900 border border-amber-300 rounded-sm px-2.5 py-1.5 hover:bg-amber-200"
                                        data-testid="outbox-reconnect-hint">
                                        <AlertCircle className="w-3.5 h-3.5" /> Überwachung nicht aktiv — hier klicken zum Reaktivieren
                                    </button>
                                )}
                            </div>
                            <div className="flex flex-col gap-2 mt-3">
                                <label className="flex items-center gap-2 text-[12px] text-gray-700 cursor-pointer select-none" data-testid="outbox-autosend-toggle">
                                    <input type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} className="w-4 h-4 accent-[#111827]" />
                                    <span>Bei <b>eindeutiger</b> Zuordnung automatisch per E-Mail senden</span>
                                </label>
                                <label className="flex items-center gap-2 text-[12px] text-gray-700 cursor-pointer select-none" data-testid="outbox-autovorgang-toggle">
                                    <input type="checkbox" checked={autoVorgang}
                                        onChange={(e) => { setAutoVorgang(e.target.checked); localStorage.setItem("tw_outbox_auto_vorgang", e.target.checked ? "1" : "0"); }}
                                        className="w-4 h-4 accent-[#111827]" />
                                    <span><b>Vorgang automatisch öffnen</b> — Lieferschein/Rechnung → Lieferschein-Scan · ZB1/COC → Scan-Erfassung · Lohn/Ausweis/Pass → Upload-Center des Verkaufs</span>
                                </label>
                            </div>
                            {folderName && (
                                <p className="text-[11px] text-gray-500 mt-3 flex items-start gap-1.5">
                                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    Unterstützt PDF und Bilder. Nach erfolgreicher Verarbeitung wird die Datei aus dem Ordner <b>gelöscht</b>.
                                    Die Überwachung läuft, solange dieser Browser-Tab geöffnet ist — Tab anpinnen und morgens einmal öffnen:
                                    die Überwachung startet dann <b>automatisch</b> wieder (Ordner &amp; Berechtigung sind gemerkt).
                                </p>
                            )}
                            <ScanProgress active={!!analyzing} fileName={analyzing?.name} phase={analyzing?.phase} testId="outbox-progress" />
                        </div>
                        {/* Nov-2026 (User-Wunsch) — Anleitung nur zeigen, solange KEINE
                            Ordner-Verknüpfung besteht; gut lesbar rechts vom Button. */}
                        {!folderName && (
                            <div className="lg:w-[26rem] shrink-0 bg-amber-50 border border-amber-200 rounded-sm p-4 text-[13px] leading-relaxed text-amber-950 space-y-2" data-testid="outbox-guide">
                                <p className="font-bold text-sm">📖 So richten Sie den Postausgang ein (1 Minute):</p>
                                <ol className="list-decimal ml-4 space-y-1.5">
                                    <li><b>Ordner anlegen:</b> Rechtsklick auf den Desktop → <i>Neu → Ordner</i> → z. B. <b>„TwoWheels-Postausgang"</b>.</li>
                                    <li>Diese Seite in <b>Chrome oder Edge</b> öffnen (Firefox/Safari unterstützen keine Ordner-Überwachung).</li>
                                    <li>Links auf <b>„Ordner verbinden &amp; überwachen"</b> klicken und den Desktop-Ordner auswählen.</li>
                                    <li>Die Browser-Frage <i>„Änderungen an Dateien zulassen?"</i> mit <b>„Änderungen zulassen"</b> bestätigen.</li>
                                    <li><b>Fertig!</b> PDF oder Foto in den Ordner legen — die KI erkennt den Dokumenttyp, öffnet den passenden Vorgang bzw. ordnet Kunde &amp; Fahrzeug zu und löscht die Datei danach aus dem Ordner.</li>
                                </ol>
                                <p className="text-[12px] text-amber-800 border-t border-amber-200 pt-2">
                                    Tipp: Tab anpinnen (Rechtsklick auf den Tab → „Tab anpinnen"). Ihr Scanner kann per <b>„Scan to Folder"</b> direkt in diesen Ordner scannen — dann läuft alles vollautomatisch.
                                    Nach einem PC-Neustart genügt ein Klick auf „Ordner verbinden &amp; überwachen" — der Ordner ist gemerkt.
                                </p>
                            </div>
                        )}
                    </div>
                </div>
                )}

                {pendingItems.length > 0 && (
                    <div className="bg-white border-2 border-amber-300 rounded-sm p-5 space-y-4" data-testid="outbox-pending-block">
                        <h2 className="font-bold text-base flex items-center gap-2 text-amber-900">
                            <AlertCircle className="w-4 h-4" /> Zuordnung bestätigen ({pendingItems.length})
                        </h2>
                        {pendingItems.map((item) => (
                            <div key={item.token} className="border border-gray-200 rounded-sm p-3 space-y-2" data-testid={`outbox-pending-${item.token}`}>
                                <div className="flex items-center justify-between gap-2 flex-wrap">
                                    <span className="inline-flex items-center gap-1.5 text-sm font-bold">
                                        <FileText className="w-4 h-4 text-gray-400" /> {item.fileName}
                                        <span className="text-[10px] font-bold uppercase bg-gray-100 text-gray-600 rounded-sm px-1.5 py-0.5">{item.document_type}</span>
                                    </span>
                                    <span className="text-[11px] text-gray-500 font-mono-num">
                                        {[item.fields?.vin && `FIN ${item.fields.vin}`, item.fields?.license_plate && `Kz ${item.fields.license_plate}`, item.fields?.last_name && `Name ${item.fields.last_name}`].filter(Boolean).join(" · ") || "keine Merkmale erkannt"}
                                    </span>
                                </div>
                                {(item.matches || []).length > 0 ? (
                                    <div className="space-y-1.5">
                                        {item.matches.map((m, i) => (
                                            <div key={i} className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-1.5 flex-wrap" data-testid={`outbox-match-${item.token}-${i}`}>
                                                <span className="text-xs inline-flex items-center gap-1.5 min-w-0">
                                                    {m.match_type === "vehicle" ? <Car className="w-3.5 h-3.5 text-emerald-600 shrink-0" /> : <UserIcon className="w-3.5 h-3.5 text-sky-600 shrink-0" />}
                                                    <b>{m.customer_name}</b>
                                                    {m.vehicle_label && <span className="text-gray-500 truncate">— {m.vehicle_label}</span>}
                                                    <span className="text-gray-400">{m.customer_email || "keine E-Mail hinterlegt"}</span>
                                                </span>
                                                <span className="flex gap-1.5 shrink-0">
                                                    {item.suggested_action === "belege_upload" && (
                                                        <Button size="sm" onClick={() => toBelege(item, m.customer_id)}
                                                            className="h-7 text-[11px] rounded-sm bg-[#111827] hover:bg-black text-[#FFCC00] font-bold"
                                                            data-testid={`outbox-commit-belege-${item.token}-${i}`}>
                                                            <ClipboardList className="w-3 h-3 mr-1" /> In Upload-Center (Verkauf)
                                                        </Button>
                                                    )}
                                                    <Button size="sm" onClick={() => commit(item, m, !!m.customer_email)}
                                                        className="h-7 text-[11px] rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                                                        data-testid={`outbox-commit-send-${item.token}-${i}`}>
                                                        <Mail className="w-3 h-3 mr-1" /> {m.customer_email ? "Archivieren + senden" : "Nur archivieren"}
                                                    </Button>
                                                    {m.customer_email && (
                                                        <Button size="sm" variant="outline" onClick={() => commit(item, m, false)}
                                                            className="h-7 text-[11px] rounded-sm font-bold"
                                                            data-testid={`outbox-commit-only-${item.token}-${i}`}>
                                                            Nur archivieren
                                                        </Button>
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-[12px] text-amber-800">Keine Zuordnung gefunden — Dokument bleibt im Ordner. Bitte Kunde/Fahrzeug prüfen (FIN/Kennzeichen im Dokument?) und Datei erneut ablegen.</p>
                                )}
                                {item.is_supplier_doc && (
                                    <div className="flex items-start gap-2 bg-sky-50 border border-sky-200 rounded-sm px-2.5 py-2" data-testid={`outbox-supplier-doc-${item.token}`}>
                                        <AlertCircle className="w-4 h-4 text-sky-700 mt-0.5 shrink-0" />
                                        <p className="text-[12px] text-sky-900">
                                            <b>Lieferanten-Beleg erkannt</b> ({item.document_type === "lieferanten_rechnung" ? "Eingangsrechnung" : "Lieferschein"}) — interner Vorgang,
                                            es wird <b>keine E-Mail an Kunden</b> versendet. Empfehlung: „Zum Lieferschein-Scan" (Lager-Buchung + Buchhaltung).
                                        </p>
                                    </div>
                                )}
                                {item.suggested_action === "belege_upload" && (
                                    <div className="flex items-start gap-2 bg-indigo-50 border border-indigo-200 rounded-sm px-2.5 py-2" data-testid={`outbox-personal-doc-${item.token}`}>
                                        <AlertCircle className="w-4 h-4 text-indigo-700 mt-0.5 shrink-0" />
                                        <p className="text-[12px] text-indigo-900">
                                            <b>Persönliches Dokument erkannt</b> ({(item.document_type || "").replace(/_/g, " ")}) —
                                            es wird <b>keine E-Mail an den Kunden</b> versendet. Empfehlung: „In Upload-Center (Verkauf)" —
                                            der Beleg wird automatisch in die „Upload Belege" des passenden Verkaufs hochgeladen.
                                        </p>
                                    </div>
                                )}
                                <div className="flex flex-wrap gap-1.5 pt-1.5 border-t border-gray-100" data-testid={`outbox-actions-${item.token}`}>
                                    <ActionBtn onClick={() => openDoc(item)} icon={Eye} label="Ansehen" testId={`outbox-view-${item.token}`} />
                                    <ActionBtn onClick={() => toDeliveryScan(item)} icon={ScanLine} label="Zum Lieferschein-Scan (Lager/Buchhaltung)" primary={!!item.is_supplier_doc} testId={`outbox-to-delivery-${item.token}`} />
                                    <ActionBtn onClick={() => toScanErfassung(item)} icon={Camera} label="Zur Scan-Erfassung (ZB1/COC)" primary={item.suggested_action === "scan_erfassung"} testId={`outbox-to-scan-erfassung-${item.token}`} />
                                    <ActionBtn onClick={() => navigate(`/dealer/workshop-orders?action=new${item.matches?.[0]?.customer_id ? `&customer=${item.matches[0].customer_id}` : ""}`)} icon={Wrench} label="Werkstatt-Auftrag anlegen" testId={`outbox-to-workshop-${item.token}`} />
                                    <ActionBtn onClick={() => navigate("/create-listing")} icon={PlusCircle} label="Fahrzeug-Inserat / Ankauf anlegen" testId={`outbox-to-listing-${item.token}`} />
                                    <ActionBtn onClick={() => setArchiveFor(archiveFor === item.token ? null : item.token)} icon={UserIcon} label="Am Kunden/Fahrzeug archivieren" testId={`outbox-to-archive-${item.token}`} />
                                    <ActionBtn onClick={() => ignoreDoc(item)} icon={Ban} label="Ignorieren" danger testId={`outbox-ignore-${item.token}`} />
                                    <ActionBtn onClick={() => deleteDoc(item)} icon={Trash2} label="Löschen" danger testId={`outbox-delete-${item.token}`} />
                                </div>
                                {archiveFor === item.token && (
                                    <ArchivePanel item={item} onArchive={(m, send) => { setArchiveFor(null); commit(item, m, send); }} />
                                )}
                                <button type="button"
                                    onClick={() => {
                                        if (embedded) { watcher?.skipCustomerItem(item); return; }
                                        setPending((prev) => prev.filter((p) => p.token !== item.token));
                                        processedRef.current.delete(item.fileName);
                                    }}
                                    className="text-[11px] text-gray-400 underline inline-flex items-center gap-1"
                                    data-testid={`outbox-skip-${item.token}`}>
                                    <XIcon className="w-3 h-3" /> Überspringen (Datei bleibt im Ordner)
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {!embedded && (
                <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="outbox-log-block">
                    <h2 className="font-bold text-base mb-3">Verlauf dieser Sitzung</h2>
                    {log.length === 0 ? (
                        <p className="text-sm text-gray-400">Noch keine Dokumente verarbeitet.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {log.map((l, i) => (
                                <li key={i} className="py-2 flex items-center gap-2 text-xs flex-wrap" data-testid={`outbox-log-${i}`}>
                                    {l.ok ? <Check className="w-3.5 h-3.5 text-green-600 shrink-0" /> : <XIcon className="w-3.5 h-3.5 text-red-500 shrink-0" />}
                                    <b>{l.name}</b>
                                    {l.ok ? (
                                        <>
                                            {l.action
                                                ? <span className="text-gray-600">— {l.action}</span>
                                                : <span>→ {l.customer}{l.vehicle ? ` (${l.vehicle})` : ""}</span>}
                                            {l.emailed && (l.emailStatus === "freigabe" ? (
                                                <span className="text-amber-700 font-bold inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5">
                                                    <Mail className="w-3 h-3" /> {l.to} — wartet in E-Mail-Freigabe
                                                </span>
                                            ) : (
                                                <span className="text-green-700 font-bold inline-flex items-center gap-1"><Mail className="w-3 h-3" /> {l.to}{l.mocked ? " (MOCK)" : ""}</span>
                                            ))}
                                            {l.removed && <span className="text-gray-400 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> aus Ordner gelöscht</span>}
                                        </>
                                    ) : (
                                        <span className="text-red-600">{l.error}</span>
                                    )}
                                    <span className="text-gray-400 ml-auto">{l.at.toLocaleTimeString("de-DE")}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                )}

                {/* Jul-2026 (User-Wunsch) — dauerhaft ignorierte Dokumente */}
                {ignoreRules.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="outbox-ignore-block">
                        <h2 className="font-bold text-base mb-1 inline-flex items-center gap-2"><Ban className="w-4 h-4 text-red-500" /> Dauerhaft ignorierte Dokumente ({ignoreRules.length})</h2>
                        <p className="text-[11px] text-gray-500 mb-3">Neu eingehende Dateien mit diesen Namen werden nicht mehr erkannt und nicht mehr verarbeitet.</p>
                        <ul className="divide-y divide-gray-100">
                            {ignoreRules.map((r) => (
                                <li key={r.id} className="py-1.5 flex items-center gap-2 text-xs" data-testid={`outbox-ignore-rule-${r.id}`}>
                                    <FileText className="w-3.5 h-3.5 text-gray-400" /> <b>{r.filename}</b>
                                    <button type="button" onClick={() => unignore(r)} className="ml-auto text-[11px] underline text-gray-500 hover:text-gray-700" data-testid={`outbox-unignore-${r.id}`}>
                                        nicht mehr ignorieren
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Jul-2026 (User-Wunsch) — Verlauf vergangener Tage (persistent) */}
                <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="outbox-history-block">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <h2 className="font-bold text-base inline-flex items-center gap-2">
                            <HistoryIcon className="w-4 h-4 text-gray-400" /> Verlauf der letzten {historyDays} Tage
                        </h2>
                        <div className="flex items-center gap-1.5">
                            {[7, 30, 90].map((d) => (
                                <button key={d} type="button" onClick={() => setHistoryDays(d)}
                                    className={`text-[11px] font-bold rounded-sm px-2 py-1 border ${historyDays === d ? "bg-[#111827] text-white border-[#111827]" : "border-gray-300 text-gray-600 hover:bg-gray-50"}`}
                                    data-testid={`outbox-history-days-${d}`}>
                                    {d} Tage
                                </button>
                            ))}
                        </div>
                    </div>
                    {history === null ? (
                        <p className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Verlauf wird geladen…</p>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-gray-400">Keine verarbeiteten Dokumente im gewählten Zeitraum.</p>
                    ) : (
                        <ul className="divide-y divide-gray-100">
                            {history.map((h, i) => (
                                <li key={i} className="py-2 flex items-center gap-2 text-xs flex-wrap" data-testid={`outbox-history-${i}`}>
                                    <span className="text-gray-400 font-mono-num shrink-0 w-28">
                                        {h.committed_at ? new Date(h.committed_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                                    </span>
                                    <a href={h.link} target="_blank" rel="noopener noreferrer"
                                        className="font-bold text-blue-700 underline inline-flex items-center gap-1 min-w-0"
                                        data-testid={`outbox-history-link-${i}`}>
                                        <FileText className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{h.filename}</span>
                                    </a>
                                    {h.document_type && h.document_type !== "unknown" && (
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-gray-100 text-gray-700 border border-gray-200 uppercase tracking-wider" data-testid={`outbox-history-type-${i}`}>
                                            {h.document_type.replace(/_/g, " ")}
                                        </span>
                                    )}
                                    {h.customer_name && <span>→ {h.customer_name}</span>}
                                    {h.scanned_by_name && <span className="text-gray-400">gescannt von {h.scanned_by_name}</span>}
                                    {h.emailed ? (h.email_status === "freigabe" ? (
                                        <span className="text-amber-700 font-bold inline-flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5">
                                            <Mail className="w-3 h-3" /> {h.recipient} — E-Mail-Freigabe
                                        </span>
                                    ) : (
                                        <span className="text-green-700 font-bold inline-flex items-center gap-1">
                                            <Mail className="w-3 h-3" /> {h.recipient}{h.emailed_mocked ? " (MOCK)" : ""}
                                        </span>
                                    )) : (
                                        <span className="text-gray-400">nur archiviert</span>
                                    )}
                                    {h.token && (
                                        <button type="button"
                                            onClick={() => setReassignToken(reassignToken === h.token ? null : h.token)}
                                            className={`ml-auto inline-flex items-center gap-1 text-[11px] font-bold rounded-sm px-2 py-1 border ${reassignToken === h.token ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "border-indigo-300 text-indigo-800 bg-indigo-50 hover:bg-indigo-100"}`}
                                            data-testid={`outbox-history-reassign-${i}`}
                                            title="KI prüft das PDF erneut auf mögliche Zuordnungen — oder manuell zuweisen">
                                            <ScanLine className="w-3 h-3" /> Neu zuordnen
                                        </button>
                                    )}
                                    {h.token && (
                                        <button type="button" onClick={() => hideEntry(h.token)}
                                            className="inline-flex items-center gap-1 text-[11px] font-bold rounded-sm px-2 py-1 border border-gray-300 text-gray-500 bg-white hover:bg-gray-100"
                                            data-testid={`outbox-history-hide-${i}`}
                                            title="Aus dem Verlauf ausblenden (nicht löschen) — über das Protokoll wieder aufrufbar">
                                            <EyeOff className="w-3 h-3" /> Ignorieren
                                        </button>
                                    )}
                                    {reassignToken === h.token && (
                                        <HistoryReassignPanel
                                            entry={h}
                                            onClose={() => setReassignToken(null)}
                                            onDone={() => { setReassignToken(null); loadHistory(historyDays); }}
                                        />
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Jul-2026 (User-Wunsch) — Protokollierung: ALLE verarbeiteten
                    Dokumente (inkl. ignorierter) mit Verarbeitungsdetails. */}
                <div className="bg-white border border-gray-200 rounded-sm p-5" data-testid="outbox-protokoll-block">
                    <h2 className="font-bold text-base inline-flex items-center gap-2 mb-3">
                        <ClipboardList className="w-4 h-4 text-gray-400" /> Protokoll — Verarbeitung der letzten {historyDays} Tage
                    </h2>
                    {protokoll === null ? (
                        <p className="text-sm text-gray-400 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Protokoll wird geladen…</p>
                    ) : protokoll.length === 0 ? (
                        <p className="text-sm text-gray-400">Keine verarbeiteten Dokumente im gewählten Zeitraum.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200">
                                        <th className="py-1.5 pr-2">Datum</th>
                                        <th className="py-1.5 pr-2">Datei</th>
                                        <th className="py-1.5 pr-2">Typ</th>
                                        <th className="py-1.5 pr-2">Zuordnung</th>
                                        <th className="py-1.5 pr-2">E-Mail</th>
                                        <th className="py-1.5 pr-2">Gescannt von</th>
                                        <th className="py-1.5">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {protokoll.map((p, i) => (
                                        <tr key={i} className={`border-b border-gray-100 ${p.hidden ? "opacity-60" : ""}`} data-testid={`outbox-protokoll-row-${i}`}>
                                            <td className="py-1.5 pr-2 whitespace-nowrap text-gray-500 font-mono-num">
                                                {p.committed_at ? new Date(p.committed_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                                            </td>
                                            <td className="py-1.5 pr-2 max-w-[220px]">
                                                <a href={p.link} target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-bold truncate block">{p.filename}</a>
                                            </td>
                                            <td className="py-1.5 pr-2 whitespace-nowrap">{p.document_type && p.document_type !== "unknown" ? p.document_type.replace(/_/g, " ") : "—"}</td>
                                            <td className="py-1.5 pr-2">
                                                {p.customer_name || "—"}{p.archived_vehicle ? " · am Fahrzeug archiviert" : ""}
                                            </td>
                                            <td className="py-1.5 pr-2 whitespace-nowrap">
                                                {p.emailed ? (p.email_status === "freigabe" ? "Freigabe ausstehend" : p.emailed_mocked ? "Demo (MOCK)" : `gesendet an ${p.recipient || "Kunde"}`) : "keine E-Mail"}
                                            </td>
                                            <td className="py-1.5 pr-2 whitespace-nowrap">{p.scanned_by_name || "—"}</td>
                                            <td className="py-1.5 whitespace-nowrap">
                                                {p.hidden ? (
                                                    <button type="button" onClick={() => unhideEntry(p.token)}
                                                        className="inline-flex items-center gap-1 text-[11px] font-bold text-indigo-700 underline"
                                                        data-testid={`outbox-protokoll-unhide-${i}`}>
                                                        <Eye className="w-3 h-3" /> ignoriert — wieder einblenden
                                                    </button>
                                                ) : (
                                                    <span className="text-green-700 font-bold">verarbeitet</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            <OutboxSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
        </div>
    );
}
